import { Actions, Async, F1 } from '@client/new/helpers/types';
import { useEffect, useMemo, useState } from 'react';
import {
  ChannelFilters,
  ChannelOptions,
  ChannelSortBase,
  DefaultGenerics,
  StreamChat,
} from 'stream-chat';
import { useInboxModel } from '../model/useInbox.model';
import uuid4 from 'uuid4';
import { chatChannelType, getChannelListOptions } from '../helpers';
import { useBlockedUsersList } from './useBlockedUsersList';
import { Author } from 'generated/graphql';
import auth from '@client/components/auth';
import { ChatApi } from '@client/api/ChatApi';
import toast from 'react-hot-toast';

export type InboxViewModelParams = {
  influencerId: number;
};

export type InboxViewModel = {
  isMessagingEnabled: boolean;
  activeTab: InboxTab;
  setTab: (value: InboxTab) => void;
  userInfoModal: {
    open: boolean;
    onClose: () => void;
    userId: number;
    username: string;
    profilePicture?: string;
    email?: string;
    marketing?: boolean | null;
  };
  chatRefreshKey: string;
  refreshKey: () => void;
  chat: {
    chatClient: StreamChat | null;
    userId: string;
    channelListOptions: ChannelListOptions;
    blockChannel: (id: string) => void;
    showUserInfo: (userId: string) => void;
  };
  enableDM: {
    submitting: boolean;
    influencerId: number;
    isBundleBusinessModel: boolean;
    submitEnable: () => void;
  };
  blockedUsersList: {
    blockedUsers: Author[];
    loadingBlockedUsers: boolean;
    unblockUser: (userId: number) => Promise<void>;
  };
};

export type InboxTab = 'chat' | 'blockedUsers';
export type InboxViewActionsMap = {
  submitEnable: null;
  setTab: InboxTab;
  showUserInfo: string;
  closeUserInfo: null;
  refreshKey: null;
  blockChannel: string | undefined;
};

export type InboxViewActions = Actions<InboxViewActionsMap>;

export type InboxViewDispatcher = F1<InboxViewActions>;

export type ChannelListOptions = {
  filters: ChannelFilters;
  options: ChannelOptions;
  sort: ChannelSortBase<DefaultGenerics>;
};

export const useInboxViewModel = (
  p: InboxViewModelParams,
): Async<InboxViewModel> => {
  const [activeTab, setActiveTab] = useState<InboxTab>('chat');
  const [chatRefreshKey, setRefreshKey] = useState<string>(uuid4());
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);
  const [streamToken, setStreamToken] = useState(null);
  const [chatChannels, setChatChannels] = useState<
    Array<{ userId: number; id: string }>
  >([]);
  const userId = `creator-${p.influencerId}`;
  const channelListOptions = useMemo(
    () => getChannelListOptions(userId, false),
    [userId],
  );

  const asyncModel = useInboxModel({ ...p, userId });

  useEffect(() => {
    if (
      !streamToken &&
      asyncModel.type === 'fetched' &&
      asyncModel.value.canDirectMessage
    ) {
      const isCreator = auth.isInfluencer();
      if (isCreator) {
        ChatApi.getCreatorChatToken().then((token) => {
          setStreamToken(token);
        });
      } else {
        ChatApi.getModeratorChatToken({ creatorId: p.influencerId }).then(
          (token) => {
            setStreamToken(token);
          },
        );
      }
    }
  }, [asyncModel.type]);

  useEffect(() => {
    if (chatClient) {
      const getChatChannels = async () => {
        const channels = await chatClient.queryChannels(
          channelListOptions.filters,
        );
        const users: Array<{ userId: string; id: string }> = [];

        channels.forEach((channel) => {
          Object.values(channel.state.members).forEach((member) => {
            member.user &&
              users.push({ userId: member.user.id, id: channel.id || '' });
          });
        });

        const usersData = users
          .filter((ch) => !ch.userId.startsWith('creator'))
          .map((ch) => ({ ...ch, userId: Number(ch.userId) }));
        setChatChannels(usersData);
      };

      getChatChannels();
    }
  }, [chatClient]);

  useEffect(() => {
    if (!chatClient && streamToken) {
      const apiKey = import.meta.env.VITE_STREAM_CHAT_ID;
      const client = new StreamChat(apiKey, undefined);

      client
        .connectUser({ id: `creator-${p.influencerId}` }, streamToken)
        .then(() => {
          setChatClient(client);
        });
    }

    return () => {
      setChatClient(null);
      chatClient && chatClient.disconnectUser();
    };
  }, [streamToken]);

  const blockedUsersList = useBlockedUsersList({
    userId,
    chatClient,
    activeTab,
  });

  if (asyncModel.type !== 'fetched') return asyncModel;

  const model = asyncModel.value;

  if (model.canDirectMessage && !streamToken) {
    return {
      type: 'fetching',
    };
  }

  const blockChannel = async (id: string | undefined) => {
    const selectedChannel = chatChannels.find((ch) => ch.id === id);
    if (!chatClient || !selectedChannel) {
      toast.error('Could not block user');
      return;
    }

    const channel = chatClient.channel(
      chatChannelType,
      selectedChannel?.id,
      {},
    );
    try {
      await channel.updatePartial({ set: { frozen: true } });
      setRefreshKey(uuid4());
    } catch (error) {
      toast.error('Could not block user');
    }
  };

  const dispatch: InboxViewDispatcher = (action) => {
    switch (action.type) {
      case 'submitEnable':
        return model.dispatch(action);
      case 'setTab':
        return setActiveTab(action.payload);
      case 'showUserInfo':
        return model.dispatch({
          type: 'showUserInfo',
          payload: Number(action.payload),
        });
      case 'closeUserInfo':
        return model.dispatch({ type: 'clearUserInfo' });
      case 'refreshKey':
        setRefreshKey(uuid4());
        return null;
      case 'blockChannel':
        blockChannel(action.payload);
        return null;
    }
  };

  return {
    type: 'fetched',
    value: {
      enableDM: {
        submitting: model.submitting,
        isBundleBusinessModel: model.isBundleBusinessModel,
        influencerId: p.influencerId,
        submitEnable: () => dispatch({ type: 'submitEnable' }),
      },
      chat: {
        channelListOptions,
        showUserInfo: (userId) =>
          dispatch({ type: 'showUserInfo', payload: userId }),
        userId,
        blockChannel: (id?: string) =>
          dispatch({ type: 'blockChannel', payload: id }),
        chatClient,
      },
      isMessagingEnabled: model.canDirectMessage,
      activeTab,
      setTab: (value) => dispatch({ type: 'setTab', payload: value }),
      userInfoModal: {
        open: !!model.userInfo?.id,
        onClose: () => dispatch({ type: 'closeUserInfo' }),
        userId: model.userInfo?.id || 0,
        username: model.userInfo?.username || '',
        profilePicture: model.userInfo?.profilePicture,
        email: model.userInfo?.email,
        marketing: model.userInfo?.marketing,
      },
      chatRefreshKey,
      refreshKey: () => dispatch({ type: 'refreshKey' }),
      blockedUsersList,
    },
  };
};
